import { useEffect, useId, useState } from 'react'
import { CART_MAX_LINE_QUANTITY } from './cart-limits'

export interface QuantityStepperProps {
  readonly value: number
  readonly min?: number
  readonly max?: number
  readonly disabled?: boolean
  /** Why the stepper is disabled (e.g. a line locked to an accepted quote).
   * When `disabled`, this is exposed to assistive tech via `aria-describedby`
   * on the input so a screen-reader user learns the reason, not just that the
   * control is unavailable (WCAG F1). */
  readonly disabledReason?: string
  /** Accessible label for the number input, e.g. "Quantity for Deep-groove ball bearing 6800ZZ". */
  readonly label: string
  /** Committed on blur / Enter / +/- click — never on every keystroke, so a
   * partially-typed value never fires a network call mid-edit. */
  readonly onChange: (next: number) => void
}

/**
 * The cart line quantity stepper (screens.md Cart §12 "qty input"). A plain
 * `<input type="number">` (native keyboard support, spinner semantics screen
 * readers already know) plus explicit +/- buttons for pointer/touch users —
 * both operable by keyboard alone, both ≥24px targets (accessibility-wcag).
 *
 * `disabled` is used for `locked` cart lines (cart-readiness.ts) — the
 * quantity is fixed to an accepted Quote and editing it here would silently
 * invalidate that price; the disabled state itself is why, not a mystery
 * greyed-out control (the caller pairs it with a Tooltip explaining that).
 */
export function QuantityStepper({
  value,
  min = 1,
  max = CART_MAX_LINE_QUANTITY,
  disabled = false,
  disabledReason,
  label,
  onChange,
}: QuantityStepperProps) {
  const [draft, setDraft] = useState(String(value))
  const [invalid, setInvalid] = useState(false)
  const inputId = useId()
  const errorId = useId()
  const disabledReasonId = useId()
  const showDisabledReason = disabled && Boolean(disabledReason)

  let describedById: string | undefined
  if (invalid) {
    describedById = errorId
  } else if (showDisabledReason) {
    describedById = disabledReasonId
  }

  useEffect(() => {
    setDraft(String(value))
  }, [value])

  function commit(next: number) {
    setInvalid(false)
    const clamped = Math.min(max, Math.max(min, Math.round(next)))
    setDraft(String(clamped))
    if (clamped !== value) {
      onChange(clamped)
    }
  }

  /**
   * Commits whatever the user actually typed into the input. `Number('')`
   * is `0`, indistinguishable from a real (and valid) typed "0" — so an
   * empty/blank draft has to be caught here, on the raw string, *before*
   * it goes through `Number(...)`, rather than relying on `NaN`/`isFinite`
   * downstream. A draft that never resolved to any digits (an
   * emptied/cleared input, or free-typed non-numeric text — the native
   * `type="number"` input strips non-numeric characters as they're typed,
   * so "abc" arrives here as "", never as a literal string) must NOT
   * silently clamp to `min` and commit — that would silently drop e.g. a
   * qty of 500 down to 1 with no warning. Revert to the last committed
   * value and surface an inline validation message instead.
   */
  function commitDraft() {
    if (draft.trim() === '') {
      setDraft(String(value))
      setInvalid(true)
      return
    }
    commit(Number(draft))
  }

  return (
    <div className="qty-stepper">
      <button
        type="button"
        className="qty-stepper-btn"
        aria-label={`Decrease ${label}`}
        disabled={disabled || value <= min}
        onClick={() => commit(value - 1)}
      >
        −
      </button>
      <input
        id={inputId}
        className="qty-stepper-input"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        aria-label={label}
        aria-invalid={invalid}
        aria-describedby={describedById}
        disabled={disabled}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commitDraft()
          }
        }}
      />
      <button
        type="button"
        className="qty-stepper-btn"
        aria-label={`Increase ${label}`}
        disabled={disabled || value >= max}
        onClick={() => commit(value + 1)}
      >
        +
      </button>
      {invalid && (
        <p id={errorId} role="alert" className="form-error-text qty-stepper-error">
          Enter a quantity — reverted to {value}.
        </p>
      )}
      {showDisabledReason && (
        <span id={disabledReasonId} className="sr-only">
          {disabledReason}
        </span>
      )}
    </div>
  )
}
